import { createMachine, assign, actions } from "xstate";
import { CaseCreationType } from "src/types";
import type { Feature, Polygon, MultiPolygon } from "geojson";

interface Context {
  name: string;
  description: string;
  userSrid: string;
  caseType: CaseCreationType;
  polygons: Feature<Polygon | MultiPolygon>[];
  runYear: number | null;
  cutoff: number | null;
  // TODO: refactor once normal case is added
  polygonList_frontendSrid: string[];
  warningList: string[];
}

type CaseCreateEvent =
  | { type: "PREVIOUS" }
  | { type: "CREATE_NORMAL" }
  | { type: "CREATE_PREDEFINED"; payload: Context }
  | { type: "NEXT"; payload: Context }
  | { type: "PREVIOUS" }
  | { type: "CLEAR_POLYGONS" }
  | { type: "CLEAR_WARNING_LIST" }
  | { type: "PICK_GEOMETRY_CREATION"; payload: Context }
  | { type: "UPLOAD_FEATURE"; payload: Context }
  | { type: "DRAW_FEATURE"; payload: Context }
  | { type: "PICK_FEATURE"; payload: Context }
  | { type: "CREATION_SUCCESS" }
  | { type: "STORE_POLYGONS"; payload: { polygons: Feature<Polygon | MultiPolygon>[] } };

const context: Context = {
  name: "",
  description: "",
  userSrid: "",
  caseType: CaseCreationType.PREDEFINED,
  polygonList_frontendSrid: [],
  polygons: [],
  runYear: null,
  cutoff: null,
  warningList: [],
};

export const caseCreateMachine = createMachine<Context, CaseCreateEvent>({
  id: "caseCreate",
  initial: "new",
  context: context,
  on: {
    CLEAR_POLYGONS: {
      actions: assign((context: Context) => {
        return { ...context, polygons: [] };
      }),
    },
  },
  states: {
    new: {
      on: {
        PREVIOUS: {
          actions: ["exit"],
        },
        CREATE_NORMAL: {
          target: "normal",
        },
        CREATE_PREDEFINED: {
          target: "predefined",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
        PICK_GEOMETRY_CREATION: {
          target: "pickGeometryCreation",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
      },
    },
    pickGeometryCreation: {
      on: {
        PREVIOUS: "#caseCreate.new",
        UPLOAD_FEATURE: {
          target: "uploadFeature",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
        DRAW_FEATURE: {
          target: "drawFeature",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
        PICK_FEATURE: {
          target: "pickFeature",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
      },
    },
    uploadFeature: {
      on: {
        PREVIOUS: "#caseCreate.pickGeometryCreation",
        NEXT: {
          target: "#caseCreate.drawFeature",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
      },
    },
    pickFeature: {
      on: {
        STORE_POLYGONS: {
          actions: assign((_ctx, event) => ({
            polygons: event.payload.polygons,
          })),
        },
        PREVIOUS: "#caseCreate.pickGeometryCreation",
        NEXT: {
          target: "#caseCreate.drawFeature",
        },
      },
    },
    drawFeature: {
      on: {
        PREVIOUS: {
          target: "#caseCreate.pickGeometryCreation",
          actions: assign((context: Context) => {
            return { ...context, warningList: [] };
          }),
        },

        CLEAR_WARNING_LIST: {
          actions: assign((context: Context) => {
            return { ...context, warningList: [] };
          }),
        },
        NEXT: {
          type: "final",
          target: "#caseCreate.submitting",
          actions: assign((context: Context, event) => {
            return { ...context, ...event.payload };
          }),
        },
      },
    },

    normal: {
      initial: "step1",
      states: {
        step1: {
          type: "final",
          on: {
            PREVIOUS: "#caseCreate.new",
            NEXT: "#caseCreate.submitting",
          },
        },
      },
    },
    predefined: {
      initial: "step1",
      states: {
        step1: {
          type: "final",
          on: {
            PREVIOUS: "#caseCreate.new",
            NEXT: {
              target: "#caseCreate.submitting",
              actions: assign((context: Context, event) => {
                return { ...context, ...event.payload };
              }),
            },
          },
        },
      },
    },

    submitting: {
      entry: actions.choose([
        {
          cond: (context) => context.caseType === CaseCreationType.NORMAL,
          actions: ["submitNormal"],
        },
        {
          cond: (context) => context.caseType === CaseCreationType.PREDEFINED,
          actions: ["submit"],
        },
      ]),
      on: {
        CREATION_SUCCESS: "created",
      },
    },
    created: {
      type: "final",
      exit: ["exit"],
    },
  },
});
